<template>
  <div class="flex flex-col gap-2 filter-input">
    <!-- Header with operators -->
    <div class="flex items-center justify-between px-3">
      <div class="flex items-center gap-1 flex-1 min-w-0">
        <span 
          class="text-sm font-semibold text_color truncate max-w-[150px]"
          :title="filterName"
        >{{ filterName }}</span>
        <FieldDropdown
          ref="operatorDropdownRef"
          :field="{ 
            name: filterName, 
            type: 'select',
            options: operatorOptions 
          }"
          v-model="selectedOperator"
          :show-search="false"
          :show-arrow="true"
          variant="2"
          max-height="600px"
          @update:modelValue="handleOperatorChange"
        />
      </div>
      <slot name="delete-icon"></slot>
    </div>

    <!-- Duration inputs -->
    <div v-if="showValueInput" class="px-3 pb-3">
      <!-- Between operator: two duration inputs -->
      <template v-if="isBetweenOperator">
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <a-input-number
              v-model:value="durationValue1"
              :min="0"
              placeholder="Min"
              class="flex-1"
              :controls="false"
              @keypress="handleKeyPress"
              @change="handleDurationChange"
              @click.stop=""
            />
            <a-select
              v-model:value="durationUnit1"
              :options="unitOptions"
              class="w-24"
              :get-popup-container="getPopupContainer"
              :dropdown-style="{ zIndex: 10000 }"
              @change="handleDurationChange"
              @click.stop=""
              @mousedown.stop=""
            />
          </div>
          <span class="text-xs secondary_color text-center">and</span>
          <div class="flex items-center gap-2">
            <a-input-number
              v-model:value="durationValue2"
              :min="0"
              placeholder="Max"
              class="flex-1"
              :controls="false"
              @keypress="handleKeyPress"
              @change="handleDurationChange"
              @click.stop=""
            />
            <a-select
              v-model:value="durationUnit2"
              :options="unitOptions"
              class="w-24"
              :get-popup-container="getPopupContainer"
              :dropdown-style="{ zIndex: 10000 }"
              @change="handleDurationChange"
              @click.stop=""
              @mousedown.stop=""
            />
          </div>
        </div>
      </template>

      <!-- Single duration input for less_than and after -->
      <template v-else>
        <div class="flex items-center gap-2">
          <a-input-number
            v-model:value="durationValue1"
            :min="0"
            :placeholder="getInputPlaceholder()"
            class="flex-1"
            :controls="false"
            @keypress="handleKeyPress"
            @change="handleDurationChange"
            @click.stop=""
          />
          <a-select
            v-model:value="durationUnit1"
            :options="unitOptions"
            class="w-24"
            :get-popup-container="getPopupContainer"
            :dropdown-style="{ zIndex: 10000 }"
            @change="handleDurationChange"
            @click.stop=""
            @mousedown.stop=""
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, nextTick } from "vue";
import {
  getOperatorsForDataType,
  filterOperators,
  requiresValue,
} from "@/src/constants/analyticsFilterConstants.js";
import FieldDropdown from "@/src/components/task_components/FieldDropdown.vue";

const props = defineProps({
  filterId: { type: String, required: true },
  filterName: { type: String, required: true },
  fieldType: { type: String, default: "select_timestamp" },
  currentValue: { type: [String, Number, Boolean, Array, Object], default: null },
});

const emit = defineEmits(["submit", "clear"]);

const operatorDropdownRef = ref(null);
const selectedOperator = ref("less_than");
const durationValue1 = ref(null);
const durationUnit1 = ref("days");
const durationValue2 = ref(null);
const durationUnit2 = ref("days");

const unitOptions = [
  { label: "Seconds", value: "seconds" },
  { label: "Minutes", value: "minutes" },
  { label: "Hours", value: "hours" },
  { label: "Days", value: "days" },
];

const getPopupContainer = () => {
  if (typeof window !== "undefined" && document?.body) {
    return document.body;
  }
  return undefined;
};

const operatorOptions = computed(() => {
  const operators = getOperatorsForDataType("select_timestamp");
  return filterOperators
    .filter((op) => operators.includes(op.id))
    .map((op) => ({
      id: op.id,
      name: op.label,
      label: op.label,
      value: op.id,
    }));
});

const showValueInput = computed(() => {
  if (!selectedOperator.value) return true;
  return requiresValue(selectedOperator.value);
});

const isBetweenOperator = computed(() => {
  return selectedOperator.value === "between" || selectedOperator.value === "not_between";
});

const getInputPlaceholder = () => {
  if (selectedOperator.value === "less_than") {
    return "Enter value";
  } else if (selectedOperator.value === "after") {
    return "Enter value";
  }
  return "Enter value";
};

// Parse duration value from object format
const parseDurationValue = (value) => {
  if (!value) return { duration: null, unit: "days" };
  if (typeof value === "object" && value.duration !== undefined) {
    return {
      duration: value.duration,
      unit: value.unit || "days",
    };
  }
  return { duration: null, unit: "days" };
};

// Initialize values from currentValue
watch(
  () => props.currentValue,
  (newValue) => {
    if (newValue && typeof newValue === "object" && newValue.operator) {
      selectedOperator.value = newValue.operator;
      
      if (newValue.operator === "between" || newValue.operator === "not_between") {
        // Between/not_between has two values
        if (Array.isArray(newValue.values) && newValue.values.length >= 2) {
          const val1 = parseDurationValue(newValue.values[0]);
          const val2 = parseDurationValue(newValue.values[1]);
          durationValue1.value = val1.duration;
          durationUnit1.value = val1.unit;
          durationValue2.value = val2.duration;
          durationUnit2.value = val2.unit;
        } else if (newValue.value && typeof newValue.value === "object") {
          // Handle {start, end} format
          const val1 = parseDurationValue(newValue.value.start);
          const val2 = parseDurationValue(newValue.value.end);
          durationValue1.value = val1.duration;
          durationUnit1.value = val1.unit;
          durationValue2.value = val2.duration;
          durationUnit2.value = val2.unit;
        }
      } else if (requiresValue(newValue.operator)) {
        const val = parseDurationValue(newValue.value);
        durationValue1.value = val.duration;
        durationUnit1.value = val.unit;
        durationValue2.value = null;
        durationUnit2.value = "days";
      } else {
        // Operator doesn't require value (is_empty, is_not_empty)
        durationValue1.value = null;
        durationUnit1.value = "days";
        durationValue2.value = null;
        durationUnit2.value = "days";
      }
    } else {
      // Default to less_than operator
      selectedOperator.value = "less_than";
      durationValue1.value = null;
      durationUnit1.value = "days";
      durationValue2.value = null;
      durationUnit2.value = "days";
    }
  },
  { immediate: true }
);

const handleOperatorChange = () => {
  nextTick(() => {
    if (operatorDropdownRef.value?.closeDropdown) {
      operatorDropdownRef.value.closeDropdown();
    }
  });

  // Reset values when switching operators
  if (!requiresValue(selectedOperator.value)) {
    durationValue1.value = null;
    durationValue2.value = null;
  } else if (selectedOperator.value !== "between" && selectedOperator.value !== "not_between") {
    durationValue2.value = null;
    durationUnit2.value = "days";
  }
};

const handleKeyPress = (event) => {
  const charCode = event.which ? event.which : event.keyCode;
  if (charCode > 31 && (charCode < 48 || charCode > 57)) {
    event.preventDefault();
    return false;
  }
  return true;
};

const handleDurationChange = () => {
};

const handleSubmit = () => {
  try {
    if (!selectedOperator.value) {
      selectedOperator.value = "less_than";
    }

    // Operators without value (is_empty, is_not_empty)
    if (!requiresValue(selectedOperator.value)) {
      emit("submit", {
        filterId: props.filterId,
        value: {
          operator: selectedOperator.value,
          value: null,
        },
      });
      return;
    }

    // Between and not_between operators
    if (selectedOperator.value === "between" || selectedOperator.value === "not_between") {
      if (durationValue1.value != null && durationValue2.value != null) {
        emit("submit", {
          filterId: props.filterId,
          value: {
            operator: selectedOperator.value,
            values: [
              { duration: durationValue1.value, unit: durationUnit1.value },
              { duration: durationValue2.value, unit: durationUnit2.value },
            ],
          },
        });
      } else if (durationValue1.value == null && durationValue2.value == null) {
        emit("clear", { filterId: props.filterId });
      }
      return;
    }

    // Single value operators (less_than, after)
    if (durationValue1.value != null) {
      emit("submit", {
        filterId: props.filterId,
        value: {
          operator: selectedOperator.value,
          value: {
            duration: durationValue1.value,
            unit: durationUnit1.value,
          },
        },
      });
    } else {
      emit("clear", { filterId: props.filterId });
    }
  } catch (error) {
    console.error("Error in handleSubmit:", error);
  }
};

const buildSubmitData = () => {
  try {
    let operator = selectedOperator.value;
    if (!operator) {
      operator = "less_than";
    }

    if (!requiresValue(operator)) {
      return {
        filterId: props.filterId,
        value: { operator, value: null },
      };
    }

    if (operator === "between" || operator === "not_between") {
      if (durationValue1.value != null && durationValue2.value != null) {
        return {
          filterId: props.filterId,
          value: {
            operator,
            values: [
              { duration: durationValue1.value, unit: durationUnit1.value },
              { duration: durationValue2.value, unit: durationUnit2.value },
            ],
          },
        };
      }
      return null;
    }

    if (durationValue1.value != null) {
      return {
        filterId: props.filterId,
        value: {
          operator,
          value: {
            duration: durationValue1.value,
            unit: durationUnit1.value,
          },
        },
      };
    }
  } catch (error) {
    console.error("Error in buildSubmitData:", error);
  }
  
  return null;
};

defineExpose({
  submit: () => {
    return buildSubmitData();
  },
});
</script>

